// src/components/ManageBook.js
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import AppHeader from "./AppHeader";
import { useUser } from "../contexts/UserContext";
import { QRCodeSVG } from "qrcode.react";

const ManageBook = () => {
    const { user, isAdmin } = useUser();
    const [book, setBook] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [authorName, setAuthorName] = useState("");
    const [bookTitle, setBookTitle] = useState("");
    const [error, setError] = useState("");
    const [showQR, setShowQR] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchBookAndChapters = async () => {
            if (!user) return;

            try {
                const bookRef = doc(db, "books", user.uid);
                const bookSnap = await getDoc(bookRef);
                if (bookSnap.exists()) {
                    const bookData = bookSnap.data();
                    setBook(bookData);
                    setAuthorName(bookData.authorName || "");
                    setBookTitle(bookData.bookTitle || "");

                    if (bookData.contents && bookData.contents.length > 0) {
                        const chapterDocs = await Promise.all(
                            bookData.contents.map((id) => getDoc(doc(db, "content", id)))
                        );

                        const chapterData = chapterDocs
                            .filter((snap) => snap.exists())
                            .map((snap) => ({ id: snap.id, ...snap.data() }));

                        setChapters(chapterData);
                    }
                }
            } catch (err) {
                console.error("Error loading book:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchBookAndChapters();
    }, [user]);

    const handleSave = async () => {
        if (!user) return;
        try {
            await updateDoc(doc(db, "books", user.uid), {
                authorName,
                bookTitle,
            });
            setBook((prev) => ({ ...prev, authorName, bookTitle }));
            setEditing(false);
        } catch (err) {
            setError("Failed to save changes.");
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
        navigate("/");
    };

    const downloadQRCode = () => {
        const svg = document.getElementById("book-qr-code");
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = 512;
            canvas.height = 512;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, 512, 512);

            URL.revokeObjectURL(url);

            const pngUrl = canvas.toDataURL("image/png");

            const downloadLink = document.createElement("a");
            downloadLink.href = pngUrl;
            downloadLink.download = `LOFL-${user.uid}.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        };

        img.src = url;
    };

    if (loading) return <div className="container mt-5">Loading book...</div>;
    if (!book) return <div className="container mt-5">No book found.</div>;

    const bookUrl = `https://libraryofforgottenlives.com/?bookid=${user.uid}`;

    return (
        <div className="container mt-5">
            <AppHeader />

            <div className="d-flex justify-content-between align-items-start">
                <div>
                    {editing ? (
                        <>
                            <input
                                type="text"
                                className="form-control mb-2"
                                value={bookTitle}
                                onChange={(e) => setBookTitle(e.target.value)}
                                placeholder="Book Title"
                            />
                            <input
                                type="text"
                                className="form-control mb-2"
                                value={authorName}
                                onChange={(e) => setAuthorName(e.target.value)}
                                placeholder="Author Name"
                            />
                            <button className="btn btn-success btn-sm me-2" onClick={handleSave}>
                                Save
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>
                                Cancel
                            </button>
                        </>
                    ) : (
                        <>
                            <h2>{book.bookTitle}</h2>
                            <h5 className="text-muted">by {book.authorName}</h5>
                            <button className="btn btn-outline-secondary btn-sm mt-2" onClick={() => setEditing(true)}>
                                Edit Book Details
                            </button>
                        </>
                    )}
                </div>
                <div>
                    {isAdmin && (
                        <button className="btn btn-warning btn-sm me-2" onClick={() => navigate("/moderate")}>
                            Moderate
                        </button>
                    )}
                    <button className="btn btn-outline-danger btn-sm" onClick={handleLogout}>
                        Logout
                    </button>
                </div>
            </div>

            <hr />

            <div className="mb-4">
                <button className="btn btn-primary me-2" onClick={() => navigate("/new-chapter")}>
                    + Add New Page
                </button>
                <button className="btn btn-outline-dark" onClick={() => setShowQR(!showQR)}>
                    {showQR ? "Hide QR Code" : "Generate QR Code"}
                </button>
            </div>

            {showQR && (
                <div className="mb-4 text-center">
                    <QRCodeSVG
                        id="book-qr-code"
                        value={bookUrl}
                        size={200}
                        includeMargin={true}
                    />
                    <div className="mt-2">
                        <button className="btn btn-sm btn-outline-secondary" onClick={downloadQRCode}>
                            Download QR Code
                        </button>
                    </div>
                </div>
            )}

            <h4>Pages</h4>
            {chapters.length === 0 ? (
                <p>No chapters yet.</p>
            ) : (
                <ul className="list-group">
                    {chapters.map((chapter) => (
                        <li
                            key={chapter.id}
                            className="list-group-item d-flex justify-content-between align-items-center"
                            onClick={() => navigate(`/chapter/${chapter.id}`)}
                            style={{ cursor: "pointer" }}
                        >
                            {chapter.Title}
                            <span className="badge bg-secondary">{chapter.Type}</span>
                        </li>
                    ))}
                </ul>
            )}

            {error && <div className="alert alert-danger mt-3">{error}</div>}
        </div>
    );
};

export default ManageBook;
